import { SectionTitle, StatCard, DeltaChip } from "./ui";
import {
  TOTAL_COMPONENTS,
  toUnit,
  gradeRateSplit,
  schoolRateSplit,
  type Variant,
} from "../data/data";

type SchoolLite = { name: string; students: number };

export function EngagementSection({
  variant,
  grades,
  schools,
  students,
}: {
  variant: Variant;
  grades: number[];
  schools: SchoolLite[];
  students: number;
}) {
  const e = variant.engagement;
  const skillsPracticed = Math.min(
    TOTAL_COMPONENTS,
    toUnit(e.standardsPracticed, "skills"),
  );
  const skillsPerStudent = toUnit(variant.histogram.medianLessons, "skills");
  const avgSkillsPracticed = Math.min(
    TOTAL_COMPONENTS,
    skillsPerStudent + Math.round(skillsPracticed * 0.07),
  );
  const avgHrs =
    students > 0 ? Math.round((e.tutoringHoursNum / students) * 10) / 10 : 0;
  const rateBoth = (base: number, format: (n: number) => string) => [
    { title: "By grade", items: gradeRateSplit(base, grades, format) },
    { title: "By school", items: schoolRateSplit(base, schools, format) },
  ];

  return (
    <section className="flex flex-col gap-4">
      <SectionTitle title="Engagement" />
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          title="Average skills practiced"
          info="Average number of learning components (skills) practiced per student in the selected period."
          value={avgSkillsPracticed}
          secondary="per student in selected period"
          chip={
            <DeltaChip delta={4} suffix="%" compareLabel="vs prior period" />
          }
          breakdowns={rateBoth(
            avgSkillsPracticed,
            (n) => `${Math.round(n)} skills`,
          )}
        />
        <StatCard
          title="Average tutoring time per student"
          info="Average 1:1 AI-tutor time per student in the selected period."
          value={`${avgHrs} hrs`}
          secondary="per student in selected period"
          chip={
            <DeltaChip delta={6} suffix="%" compareLabel="vs prior period" />
          }
          breakdowns={rateBoth(avgHrs, (n) => `${n.toFixed(1)} hrs`)}
        />
        <StatCard
          title="Average skills mastered"
          info="Average learning components (skills) mastered per student in the selection."
          value={skillsPerStudent}
          secondary="per student, this school year"
          chip={
            <DeltaChip delta={3} suffix="%" compareLabel="vs prior period" />
          }
          breakdowns={rateBoth(
            skillsPerStudent,
            (n) => `${Math.round(n)} skills`,
          )}
          popSide="left"
        />
      </div>
    </section>
  );
}
